/**
 * AddToWaitlistPanel
 * ──────────────────────────────────────────────────────────────────────
 * Slide-in (Dialog) panel used by the Waitlist tab entry point
 * (Edit 4 of scheduler_edits_v2.md).
 *
 * Fields per spec:
 *   1. Patient search (name / DOB / phone)
 *   2. Reason (appointment type dropdown)
 *   3. Provider preference (optional)
 *   4. Arrival time estimate — 4 chip selector
 *   5. Notes field (optional, 1 line)
 *   6. [Add to waitlist] button
 *
 * Pure UI — the caller owns state (demo fixtures vs real API).
 */
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../ui/dialog';
import { Button } from '../../../ui/button';
import { cn } from '../../../../lib/utils';
import { Sparkles } from 'lucide-react';

const ARRIVAL_OPTIONS = [
  { id: '<15', label: 'Nearby (<15 min)' },
  { id: '15-30', label: '15–30 min away' },
  { id: '30-60', label: '30–60 min away' },
  { id: '60+', label: 'More than an hour away' },
];

const FieldLabel = ({ children }) => (
  <p className="text-[10px] text-slate-500 mb-1">{children}</p>
);

const TextInput = ({ value, onChange, placeholder, testId }) => (
  <input
    type="text"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className="w-full px-3 py-2 text-xs outline-none"
    style={{
      background: '#f9f9f9',
      border: '0.5px solid #ebebeb',
      borderRadius: 8,
    }}
    data-testid={testId}
  />
);

const AddToWaitlistPanel = ({
  open,
  onOpenChange,
  onSubmit,
  visitTypes = [],
  providers = [],
  defaults = {},
}) => {
  const [patient, setPatient] = useState('');
  const [reason, setReason] = useState('');
  const [providerPref, setProviderPref] = useState('');
  const [arrival, setArrival] = useState('<15');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open) return;
    setPatient(defaults.patient || '');
    setReason(defaults.reason || (visitTypes[0]?.id || ''));
    setProviderPref(defaults.providerPref || '');
    setArrival(defaults.arrival || '<15');
    setNotes(defaults.notes || '');
  }, [open, defaults.patient, defaults.reason, defaults.providerPref, defaults.arrival, defaults.notes, visitTypes]);

  const submit = () => {
    if (!patient.trim()) return;
    onSubmit?.({
      patient: patient.trim(),
      reason,
      providerPref: providerPref || null,
      arrival,
      notes: notes.trim() || null,
    });
    onOpenChange?.(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="add-waitlist-panel">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold">Add to waitlist</DialogTitle>
          <DialogDescription className="text-[11px] text-slate-500">
            Capture the patient&apos;s details to rank them against open slots.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <FieldLabel>Patient (name / DOB / phone)</FieldLabel>
            <TextInput
              value={patient}
              onChange={setPatient}
              placeholder="Search by name, DOB or phone…"
              testId="waitlist-patient-search"
            />
          </div>
          <div>
            <FieldLabel>Reason</FieldLabel>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 text-xs outline-none"
              style={{ background: '#f9f9f9', border: '0.5px solid #ebebeb', borderRadius: 8 }}
              data-testid="waitlist-reason"
            >
              <option value="">Select visit type…</option>
              {visitTypes.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <FieldLabel>Provider preference (optional)</FieldLabel>
            <select
              value={providerPref}
              onChange={(e) => setProviderPref(e.target.value)}
              className="w-full px-3 py-2 text-xs outline-none"
              style={{ background: '#f9f9f9', border: '0.5px solid #ebebeb', borderRadius: 8 }}
              data-testid="waitlist-provider-pref"
            >
              <option value="">No preference</option>
              {providers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <FieldLabel>
              Ask the patient: if we called them now with an open slot, how quickly could they arrive?
            </FieldLabel>
            <p className="text-[9px] text-slate-400 -mt-0.5 mb-1.5 inline-flex items-center gap-1">
              <Sparkles className="h-2.5 w-2.5 text-violet-500" />
              Echo AI uses this answer to auto-rank the waitlist for the best open slot.
            </p>
            <div className="flex flex-wrap gap-1.5" data-testid="waitlist-arrival-chips">
              {ARRIVAL_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setArrival(opt.id)}
                  className={cn(
                    'px-3 py-1.5 text-[10px] rounded-full transition-colors',
                    arrival === opt.id
                      ? 'bg-[#111] text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  )}
                  style={{ border: 'none', cursor: 'pointer' }}
                  data-testid={`waitlist-arrival-${opt.id}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <FieldLabel>Notes (optional)</FieldLabel>
            <TextInput
              value={notes}
              onChange={setNotes}
              placeholder=""
              testId="waitlist-notes"
            />
          </div>
        </div>
        <DialogFooter className="gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={() => onOpenChange?.(false)}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            className="text-xs bg-[#111] hover:bg-[#222]"
            onClick={submit}
            disabled={!patient.trim()}
            data-testid="waitlist-submit"
          >
            Add to waitlist
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddToWaitlistPanel;
